/**
 * Website → ❤️ BP tab: blood pressure readings for a person (from the phone
 * app or typed here): a chart with the person's limits, then the readings by
 * date in Morning / Afternoon / Night (same view as before in the Daily log,
 * with owner export and Remove), the average and "Add a reading".
 */
import { useEffect, useMemo, useState } from 'react';
import { addDays, BP_LEVEL_TEXT, bpLevel, bpScheduleFor, formatDateISO, formatTime12, localTodayISO, parseDateISO } from '@bhorosha/core';
import type { BhStore } from '@bhorosha/data';
import { Notice, Spinner } from '../ui/components';
import { BpChart, BpTab } from './DailyLog';
import { datesBetween } from './model';
import { useMonitor } from './useMonitor';
import { BpForm } from './BpForm';

const PERIODS = [7, 30, 90, 365];

export function BpPage({ store, familyId, active }: { store: BhStore; familyId: string; active: boolean }) {
  const [days, setDays] = useState(30);
  const today = localTodayISO();
  const from = formatDateISO(addDays(parseDateISO(today)!, -(days - 1)));
  const { snap, people, loading, error, now, reload } = useMonitor(store, familyId, from, today, active);
  const [personId, setPersonId] = useState<string>(() => window.state?.activePersonId || '');
  const canEdit = store.role !== 'viewer';
  // The owner can export, and remove any reading (others only the readings they saved).
  const isOwner = store.role === 'owner' || window.__bhMe?.role === 'owner';

  useEffect(() => {
    const on = (e: Event) => { const id = (e as CustomEvent).detail; if (id) setPersonId(id); };
    window.addEventListener('bh:person', on);
    return () => window.removeEventListener('bh:person', on);
  }, []);
  const pd = people.find(p => p.person.id === personId) || people[0];
  const readings = useMemo(() => (pd ? snap.bp.filter(b => b.patientId === pd.person.id).sort((a, b) => b.measuredAt.localeCompare(a.measuredAt)) : []), [snap, pd]);
  if (!active) return null;
  if (!pd) return <p className="bh-muted">No people yet.</p>;
  const sched = bpScheduleFor(pd.person);
  const avg = readings.length ? [Math.round(readings.reduce((s, r) => s + r.systolic, 0) / readings.length), Math.round(readings.reduce((s, r) => s + r.diastolic, 0) / readings.length)] : null;

  return (
    <div className="bh-log">
      <div className="bh-log-tools no-print">
        <select value={pd.person.id} onChange={e => setPersonId(e.target.value)} aria-label="Person">
          {people.map(p => <option key={p.person.id} value={p.person.id}>{p.person.name || 'Unnamed'}</option>)}
        </select>
        <div className="bh-seg">{PERIODS.map(d => <button key={d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>{d === 365 ? '1 year' : `${d} days`}</button>)}</div>
        <span className="bh-log-spacer" /><span className="bh-live">● Live</span>
      </div>
      {error && <Notice tone="warn">Could not load readings: {error}</Notice>}
      <div className="bh-small bh-muted" style={{ margin: '0 0 10px' }}>
        {sched.enabled && sched.times.length
          ? <>Checks planned at {sched.times.map(formatTime12).join(' and ')}{sched.days.length ? ` on ${sched.days.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}` : ' every day'}. </>
          : <>No BP reminders. </>}
        High from {sched.limits.high_sys}/{sched.limits.high_dia}, very high from {sched.limits.very_high_sys}/{sched.limits.very_high_dia}, low at {sched.limits.low_sys}/{sched.limits.low_dia}. The family owner sets these levels and the check times in Family & members → Family.
      </div>
      {loading ? <Spinner /> : (
        <div className="bh-log-grid">
          <div>
            {readings.length > 0 && <BpChart readings={readings} person={pd.person} height={220} />}
            <BpTab people={[pd]} dates={datesBetween(from, today)} snap={snap} now={now} familyId={familyId}
              canExport={isOwner} showName={false} canEdit={canEdit} onChanged={reload} />
          </div>
          <aside className="bh-log-side">
            {avg && <div className="bh-box"><h4>Average ({readings.length} readings)</h4><div className="bh-big-num">{avg[0]}/{avg[1]}</div>
              <span className={`bh-st bp-${bpLevel(avg[0], avg[1], sched.limits)}`}>{BP_LEVEL_TEXT[bpLevel(avg[0], avg[1], sched.limits)].word}</span></div>}
            {canEdit && <BpForm familyId={familyId} personId={pd.person.id} limits={sched.limits} onSaved={reload} />}
          </aside>
        </div>
      )}
    </div>
  );
}
